import React from 'react';

export default function AboutPage() {
  const aboutCards = [
    {
      title: "Our Mission",
      description: "To empower individuals with the skills they need to succeed in the digital economy.",
      icon: (
        <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      bgColor: "bg-amber-50",
      borderColor: "border-amber-100"
    },
    {
      title: "Our Vision",
      description: "To become the leading platform for practical, industry-relevant education worldwide.",
      icon: (
        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      bgColor: "bg-orange-50",
      borderColor: "border-orange-100"
    }
  ];

  return (
    <section id="about" className="py-16 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-amber-50 opacity-60 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 rounded-full bg-orange-50 opacity-60 blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      
      {/* Decorative large quote mark */}
      <div className="absolute top-10 left-10 text-[200px] leading-none text-orange-50/50 font-serif font-black select-none pointer-events-none z-0">"</div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          <div className="text-left">
            <h2 className="text-3xl md:text-4xl flex items-center justify-center font-extrabold text-gray-900 mb-6 leading-tight">
              About <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">Us</span>
            </h2>
            <div className="w-20 h-1.5 mx-auto bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mb-8"></div>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-justify">
              We create powerful learning experiences that help students and professionals upgrade their skills and build successful careers. Our comprehensive approach blends theoretical knowledge with practical, hands-on applications to ensure you are fully prepared for the digital economy.
            </p>

            <div className="flex flex-wrap gap-4 mt-8">
              <div className="flex items-center gap-3 bg-gray-50 py-2 px-4 rounded-xl border border-gray-100">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">✓</div>
                <span className="text-sm font-semibold text-gray-700">Expert Guidance</span>
              </div>
              <div className="flex items-center gap-3 bg-gray-50 py-2 px-4 rounded-xl border border-gray-100">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm">✓</div>
                <span className="text-sm font-semibold text-gray-700">Practical Skills</span>
              </div>
            </div>
          </div>

          {/* Right Column - Mission & Vision Cards */}
          <div className="space-y-6 relative mt-4 lg:mt-0">
            {aboutCards.map((card, index) => (
              <div
                key={card.title}
                className="relative pl-0 md:pl-16 group"
              >
                {/* Line segment to next card */}
                {index !== aboutCards.length - 1 && (
                  <div className="hidden md:block absolute left-8 top-16 w-1 h-[calc(100%+1.5rem)] bg-gradient-to-b from-amber-200 to-orange-200 z-0"></div>
                )}
                {/* Timeline dot for desktop */}
                <div className="hidden md:flex absolute left-[26px] top-[60px] w-4 h-4 rounded-full bg-white border-4 border-amber-500 z-10 group-hover:scale-150 transition-transform duration-300 group-hover:border-orange-500"></div>

                <div className="p-6 md:p-8 rounded-3xl border border-gray-100 transition-all duration-300 hover:-translate-y-2 bg-white hover:border-amber-200 relative overflow-hidden group-hover:bg-amber-50/20">
                  <div className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-400 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  <div className={`absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-60 ${card.bgColor} -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-110`}></div>

                  <div className="flex flex-col sm:flex-row items-start gap-6 relative z-10">
                    <div className={`shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center ${card.bgColor} border ${card.borderColor} transition-all duration-300 group-hover:rotate-6`}>
                      {card.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors duration-300">
                        {card.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}